import { Box, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { Pressable, View } from 'react-native';
import { FlashList, type FlashListRef, type ListRenderItemInfo } from '@shopify/flash-list';
import {
  type RecommendedListing,
  FeatureType,
  type GetRecommendationsWithSimilarityQueryDTO,
  type InteractionType,
} from 'app/types/recommender';
import {
  usePastInteractions,
  usePredictions,
  useUserItemPredictions,
  useMixedRecommendations,
  useTestRecommendations,
  useOneToManySimilarListings,
  useMatchNeedsWithOffers,
  recommendationKeys,
} from 'app/network/recommender';
import { useAuthorize } from 'app/hooks/use-authorize';
import React, { useEffect, useRef, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FeatureFlag, isFeatureDisabled, isFeatureEnabled } from 'app/utils/helpers';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useUser } from 'app/network/user';
import { Event, type EventProperties, track } from 'app/lib/analytics';
import { env } from 'app/lib/env';
import type { NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import { ListingsSortBy, ListingType } from 'app/types/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { useAtomValue } from 'jotai';
import { bffUserProfileLoadedAtom, isLoggedInAtom } from 'app/atoms';
import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import { useAllGroups } from 'app/network/groups';
import type { Listing, FindAllListingsDTO } from 'app/types/listing';
import {
  type ClosestListingsByTag,
  useAllFavourites,
  useAllListings,
  useClosestOffersByTag,
  useSelectedGroupsAndAddresses,
  useTopListings,
  useUrgentNeeds,
} from 'app/network/listing';
import { type RecentLoanListing, useRecentLoans } from 'app/network/loan';
import { useAllPlaces } from 'app/network/sharing-place';
import { type BffResult } from 'app/types/bff';
import { StorageKey, useStoredValue2 } from 'app/lib/storage';
import { useAllAddresses } from 'app/network/address';
import { getRecommendationTitleKey, isApiStrategy, Strategies } from './recommended-listings.utils';

import { HorizontalListingsSkeleton } from './horizontal-listings-skeleton';
import { type SharingPlace } from 'app/types/sharing-place';
import { ListingOfferCardBase } from 'app/components/listing-offer-card.base';
import Animated, { LinearTransition } from 'react-native-reanimated';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';

export { getRecommendationTitleKey, isApiStrategy, Strategies } from './recommended-listings.utils';
const theme = createDSTheme();

/** Wraps useAllListings (infinite query) to return a flat Listing[] compatible with ApiStrategy. */
function useAllListingsFlat(
  filters: FindAllListingsDTO | undefined,
  triggeredBy: string,
  enabled: boolean,
): UseQueryResult<Listing[]> {
  const q = useAllListings(filters, triggeredBy, { enabled: enabled && !!filters });
  return { ...q, data: q.data?.pages.flatMap((p) => p.results) } as unknown as UseQueryResult<Listing[]>;
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const LANDING_PAGE_REQUEST_CARD_WIDTH = 340;
const RECOMMENDATION_LIST_DRAW_DISTANCE = 1000;
const RECOMMENDATION_ITEM_SEPARATOR_WIDTH = theme.spacing['spacing.general.x3'];
const RECOMMENDATION_ITEM_LAYOUT_TRANSITION = LinearTransition.duration(160);

const isNewListing = (createdAt: string) => new Date(createdAt).getTime() > Date.now() - ONE_WEEK_MS;

function RecommendationItemSeparator() {
  return <View style={{ width: RECOMMENDATION_ITEM_SEPARATOR_WIDTH }} />;
}

const RecommendedCarouselListingCard = function RecommendedCarouselListingCard(props: ListingOfferCardSmallProps) {
  return <ListingOfferCardBase {...props} presentation="recommended-carousel" variant="small" />;
};

type ListingOfferCardSmallProps = Omit<React.ComponentProps<typeof ListingOfferCardBase>, 'presentation' | 'variant'>;

const NEIGHBOUR_STRATEGIES = new Set<Strategies>([Strategies.NeighbourOffers, Strategies.NeighbourRequests]);
const isNeighbourStrategy = (s: Strategies) => NEIGHBOUR_STRATEGIES.has(s);
const SHARING_PLACE_STRATEGIES = new Set<Strategies>([Strategies.NearestSharingPlace]);
const isSharingPlaceStrategy = (s: Strategies) => SHARING_PLACE_STRATEGIES.has(s);
interface RecommendationContext {
  recommendationType: ListingType;
  similarityType: FeatureType;
  listingId?: string;
  listingIds?: string[];
  useBff?: boolean;
  top_n?: number;
}

const getBrowsedRecommendedListingParameters = (
  params: EventProperties[Event.BrowseRecommendedListings],
): EventProperties[Event.BrowseRecommendedListings] => ({
  user_id: params.user_id,
  listing_type: params.listing_type,
  strategy: params.strategy,
});

const getClickedRecommendedListingParameters = (
  params: EventProperties[Event.ClickedRecommendedListing],
): EventProperties[Event.ClickedRecommendedListing] => ({
  user_id: params.user_id,
  listing_id: params.listing_id,
  listing_type: params.listing_type,
  listing: params.listing,
  predicted_score: params.predicted_score,
  strategy: params.strategy,
});

type RecommenderStrategyResult = UseQueryResult<RecommendedListing[]>;
type ApiStrategyResult =
  | UseQueryResult<Listing[]>
  | UseQueryResult<RecentLoanListing[]>
  | UseQueryResult<ClosestListingsByTag[]>
  | UseQueryResult<SharingPlace[]>;
type RecommendationListItem = Listing | RecommendedListing | RecentLoanListing | SharingPlace;

function getRecommendationItemBaseKey(item: RecommendationListItem, strategy: Strategies) {
  return `${strategy}-${item.id}`;
}

interface SelectedStrategyHookArgs extends RecommendationContext {
  strategy: Strategies;
  enabled: boolean;
  tagIds?: string[];
  filters?: FindAllListingsDTO;
  top_n?: number;
}

type StrategyQueryResult = ApiStrategyResult | RecommenderStrategyResult;

function useYourFavouritesStrategy({ enabled }: SelectedStrategyHookArgs) {
  return useAllFavourites({ enabled });
}

function useMostPopularStrategy({ enabled }: SelectedStrategyHookArgs) {
  return useTopListings({ enabled });
}

function useUrgentNeedsStrategy({ enabled }: SelectedStrategyHookArgs) {
  return useUrgentNeeds({ enabled });
}

function useRecentOffersStrategy({ enabled }: SelectedStrategyHookArgs) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();

  return useAllListingsFlat(
    {
      type: ListingType.offer,
      sortBy: ListingsSortBy.Date,
      take: 20,
      addressIds: selectedAddresses,
      groupIds: selectedGroups,
    },
    'LandingPage-RecentOffers',
    enabled && isLoggedIn && loaded,
  );
}

function useRecentLoansStrategy({ enabled }: SelectedStrategyHookArgs) {
  return useRecentLoans({ enabled }) as UseQueryResult<RecentLoanListing[]>;
}

function useYourInterestStrategy({ enabled, tagIds }: SelectedStrategyHookArgs) {
  return useClosestOffersByTag(tagIds, { enabled });
}

function useByCategoryStrategy({ enabled, filters }: SelectedStrategyHookArgs) {
  return useAllListingsFlat(filters, 'LandingPage-ByCategory', enabled);
}

function useNeighbourOffersStrategy({ enabled, filters }: SelectedStrategyHookArgs) {
  return useAllListingsFlat(filters, 'NeighbourProfile-Offers', enabled);
}

function useNeighbourRequestsStrategy({ enabled, filters }: SelectedStrategyHookArgs) {
  return useAllListingsFlat(filters, 'NeighbourProfile-Requests', enabled);
}

function useNearestSharingPlaceStrategy({ enabled }: SelectedStrategyHookArgs) {
  return useAllPlaces({ enabled }) as UseQueryResult<SharingPlace[]>;
}

function useUserItemPredictionsStrategy({
  recommendationType,
  similarityType,
  enabled,
  useBff,
}: SelectedStrategyHookArgs) {
  return useUserItemPredictions(recommendationType, similarityType, { enabled }, useBff);
}

function usePredictionsStrategy({ recommendationType, enabled, useBff }: SelectedStrategyHookArgs) {
  return usePredictions(recommendationType, { enabled }, useBff);
}

function usePastInteractionsStrategy({ recommendationType, enabled, useBff }: SelectedStrategyHookArgs) {
  return usePastInteractions(recommendationType, { enabled }, useBff);
}

function useTestRecommendationsStrategy({ recommendationType, enabled, useBff }: SelectedStrategyHookArgs) {
  return useTestRecommendations(recommendationType, { enabled }, useBff);
}

function useMixedRecommendationsStrategy({
  recommendationType,
  similarityType,
  enabled,
  useBff,
}: SelectedStrategyHookArgs) {
  return useMixedRecommendations(recommendationType, similarityType, { enabled }, useBff);
}

function useOneToManySimilarListingsStrategy({
  recommendationType,
  similarityType,
  listingId,
  enabled,
  useBff,
  top_n,
}: SelectedStrategyHookArgs) {
  return useOneToManySimilarListings(recommendationType, similarityType, listingId ?? '', { enabled }, useBff, top_n);
}

function useMatchNeedsWithOffersStrategy({
  recommendationType,
  similarityType,
  enabled,
  useBff,
}: SelectedStrategyHookArgs) {
  return useMatchNeedsWithOffers(recommendationType, similarityType, { enabled }, useBff);
}

interface RecommendationsLoadedUsingBff {
  strategy: Strategies;
  recommendationType: ListingType;
  similarityType: FeatureType | InteractionType;
  bff: {
    key: BffResult['queryKey'];
    queryKey: readonly unknown[];
    queryParams?: GetRecommendationsWithSimilarityQueryDTO;
  };
}

/**
 * Defines which recommendations are loaded using the BFF system.
 *
 * @returns `{ entries, isFirstLoad, loaded }` — `entries` is undefined while filter
 * preferences haven't settled, [] on first-load / feature-disabled / not-logged-in,
 * or the BFF recommendation list once ready.  `isFirstLoad` signals that groups/addresses
 * data hasn't arrived yet (treat as pending).  `loaded` is the inner
 * useSelectedGroupsAndAddresses `loaded` value, needed by callers to gate their own
 * isPending correctly.
 */
export function useLoadRecommentationsUsingBff() {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { data: allGroups } = useAllGroups(true);
  const { data: allAddresses } = useAllAddresses();
  const { selectedGroups, selectedAddresses, loaded } = useSelectedGroupsAndAddresses();
  const { usedDefault: hasNoAddressesFilters, prefLoaded: addressesFiltersPrefLoaded } = useStoredValue2(
    StorageKey.ListingsFiltersAddresses,
  );
  const isFirstLoad =
    (hasNoAddressesFilters || addressesFiltersPrefLoaded) && !allGroups?.length && !allAddresses?.length;

  const entries = (() => {
    if (isFirstLoad) {
      // This is the first load - we don't have addresses & groups loaded yet, so don't load any recomm. using BFF
      return [];
    }

    if (isFeatureDisabled(FeatureFlag.showRecommendedForYou) || !isLoggedIn) {
      return [];
    }

    if (!loaded) {
      return undefined;
    }

    const recoms: RecommendationsLoadedUsingBff[] = [];

    const addRecomToBff = (
      strategy: Strategies,
      recommendationType: ListingType,
      bffKey: BffResult['queryKey'],
      queryKeyPrefix: string,
    ) => {
      const queryParams: GetRecommendationsWithSimilarityQueryDTO = {
        addressIds: selectedAddresses,
        groupIds: selectedGroups,
        type: recommendationType,
        similarityType: FeatureType.CONTENT,
      };

      recoms.push({
        strategy,
        recommendationType,
        similarityType: queryParams.similarityType,
        bff: {
          key: bffKey,
          queryKey: recommendationKeys.list(queryKeyPrefix, { ...queryParams }),
          queryParams,
        },
      });
    };

    addRecomToBff(
      Strategies.UseMixedRecommendations,
      ListingType.offer,
      'get_user_mixed_recommendations_offers',
      'mixed-recommendations',
    );

    // Temporarily : UseMatchNeedsWithOffers is only enabled, in PROD, for users in "Le Partage Club" group
    const groupIdLPC = 'f7c87f79-1155-49ae-8e0c-66b048116e87';
    const isLPCgroup = allGroups?.some((g) => g.id === groupIdLPC) ?? false;
    if (isLPCgroup || env.APP_ENV !== 'production') {
      addRecomToBff(
        Strategies.UseMatchNeedsWithOffers,
        ListingType.request,
        'get_user_match_needs_with_offers',
        'need-match',
      );
    }

    // @TODO When adding new entries using addRecomToBff(), you will need to update the API to support the new bffKey

    return recoms;
  })();

  return { entries, isFirstLoad, loaded, isLoggedIn };
}

export type RecommendationRuntime = ReturnType<typeof useRecommendationRuntime>;

/** Shared recommendation state that the landing page computes once for all sections. */
export function useRecommendationRuntime() {
  const { entries: loadRecommendationsUsingBff, isFirstLoad, loaded, isLoggedIn } = useLoadRecommentationsUsingBff();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  return { loadRecommendationsUsingBff, isFirstLoad, loaded, isLoggedIn, bffUserProfileLoaded };
}

export interface RecommendedListingsComponentProps {
  strategy: Strategies;
  recommendationType?: ListingType;
  similarityType?: FeatureType;
  listingId?: string;
  listingIds?: string[];
  tagIds?: string[];
  userId?: string;
  filters?: FindAllListingsDTO;
  enabled?: boolean; // Only used for YourInterest strategy to disable the query when the user has no interests (tags)
  top_n?: number;
}

type RecommendationPropsForStrategy<S extends Strategies> = Omit<RecommendedListingsComponentProps, 'strategy'> & {
  strategy: S;
};

interface ResolvedRecommendationStrategy {
  enabled: boolean;
  hookArgs: SelectedStrategyHookArgs;
  isApi: boolean;
  isNeighbour: boolean;
  isSharingPlace: boolean;
  queryDisabled: boolean;
  strategyEnabled: boolean;
  useBff: boolean;
}

function resolveRecommendationStrategy(
  strategyProps: RecommendedListingsComponentProps,
  runtime: RecommendationRuntime,
): ResolvedRecommendationStrategy {
  const {
    strategy,
    recommendationType,
    similarityType,
    listingId,
    listingIds,
    tagIds,
    filters,
    top_n,
    enabled: strategyEnabledProp,
  } = strategyProps;
  const { loadRecommendationsUsingBff, isLoggedIn } = runtime;
  const isApi = isApiStrategy(strategy);
  const isNeighbour = isNeighbourStrategy(strategy);
  const isSharingPlace = isSharingPlaceStrategy(strategy);
  const useBff =
    !isApi &&
    Boolean(
      loadRecommendationsUsingBff?.find(
        (recommendation: RecommendationsLoadedUsingBff) =>
          recommendation.strategy === strategy &&
          recommendation.recommendationType === recommendationType &&
          recommendation.similarityType === similarityType,
      ),
    );
  const queryDisabled = strategy === Strategies.UseMatchNeedsWithOffers && !useBff;
  const strategyEnabled = strategyEnabledProp ?? true;
  const featureEnabled =
    isNeighbour ||
    isSharingPlace ||
    strategy === Strategies.ByCategory ||
    isFeatureEnabled(FeatureFlag.showRecommendedForYou);
  const apiEnabled = featureEnabled && strategyEnabled && !queryDisabled;
  const recommenderEnabled = Boolean(
    featureEnabled && isLoggedIn && loadRecommendationsUsingBff && !queryDisabled && strategyEnabled,
  );
  const enabled = isApi ? apiEnabled : recommenderEnabled;

  if (!isApi && (!recommendationType || !similarityType)) {
    console.error('[recommendations] recommendationType and similarityType are required for non-API strategy', {
      strategy,
      recommendationType,
      similarityType,
    });
  }

  return {
    enabled,
    hookArgs: {
      strategy,
      recommendationType: recommendationType ?? ListingType.offer,
      similarityType: similarityType ?? FeatureType.CONTENT,
      listingId,
      listingIds,
      useBff,
      enabled,
      tagIds,
      filters,
      top_n,
    },
    isApi,
    isNeighbour,
    isSharingPlace,
    queryDisabled,
    strategyEnabled,
    useBff,
  };
}

function getRecommendationQueryState(
  runtime: RecommendationRuntime,
  resolvedStrategy: ResolvedRecommendationStrategy,
  strategyResult: StrategyQueryResult,
) {
  const { loadRecommendationsUsingBff, isFirstLoad, loaded, isLoggedIn, bffUserProfileLoaded } = runtime;
  const { enabled, isApi, isNeighbour, isSharingPlace, queryDisabled, strategyEnabled, useBff } = resolvedStrategy;
  const recommendations = strategyResult.data;
  const isFetching = strategyResult.isFetching;
  const isQueryPending = strategyResult.isPending;
  const refetchQuery = strategyResult.refetch;
  const show = Boolean(enabled && recommendations && recommendations.length >= 1);

  // Pending means the selected query could still produce a visible recommendation section.
  const isPending =
    strategyEnabled &&
    (isNeighbour || isSharingPlace || (isFeatureEnabled(FeatureFlag.showRecommendedForYou) && isLoggedIn)) &&
    !queryDisabled &&
    (isApi
      ? isFetching || isQueryPending
      : loadRecommendationsUsingBff === undefined ||
        isFirstLoad ||
        !loaded ||
        (useBff && !bffUserProfileLoaded) ||
        isFetching ||
        isQueryPending);

  return { show, recommendations, isPending, refetchQuery, count: recommendations?.length ?? 0 };
}

// Keep these as explicit static hook boundaries: selecting a hook from a runtime map breaks
// the Rules of Hooks, while calling every hook mounts an observer for every strategy.
export function useUrgentNeedsRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.UrgentNeeds>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useUrgentNeedsStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useRecentLoansRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.RecentLoans>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useRecentLoansStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useRecentOffersRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.RecentOffers>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useRecentOffersStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useMixedRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.UseMixedRecommendations>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useMixedRecommendationsStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useNearestSharingPlaceRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.NearestSharingPlace>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useNearestSharingPlaceStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useMatchingRequestsRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.UseMatchNeedsWithOffers>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useMatchNeedsWithOffersStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useYourInterestRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.YourInterest>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useYourInterestStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useMostPopularRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.MostPopular>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useMostPopularStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useFavouritesRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.YourFavourites>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useYourFavouritesStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export function useSimilarListingsRecommendationsWithRuntime(
  strategyProps: RecommendationPropsForStrategy<Strategies.UseOneToManySimilarListings>,
  runtime: RecommendationRuntime,
) {
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  const strategyResult = useOneToManySimilarListingsStrategy(resolvedStrategy.hookArgs);
  return getRecommendationQueryState(runtime, resolvedStrategy, strategyResult);
}

export type RecommendationQueryState = ReturnType<typeof getRecommendationQueryState>;

interface StrategyQueryProps {
  runtime: RecommendationRuntime;
  resolvedStrategy: ResolvedRecommendationStrategy;
  children: (state: RecommendationQueryState) => React.ReactNode;
}

// Each strategy has a static component boundary so only its own query observer mounts.
function YourFavouritesQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useYourFavouritesStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function MostPopularQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useMostPopularStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function UrgentNeedsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useUrgentNeedsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function RecentOffersQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useRecentOffersStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function RecentLoansQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useRecentLoansStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function YourInterestQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useYourInterestStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function ByCategoryQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useByCategoryStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function NeighbourOffersQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useNeighbourOffersStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function NeighbourRequestsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useNeighbourRequestsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function NearestSharingPlaceQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useNearestSharingPlaceStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function UserItemPredictionsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useUserItemPredictionsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function PredictionsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = usePredictionsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function PastInteractionsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = usePastInteractionsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function TestRecommendationsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useTestRecommendationsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function MixedRecommendationsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useMixedRecommendationsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function OneToManySimilarListingsQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useOneToManySimilarListingsStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

function MatchNeedsWithOffersQuery({ runtime, resolvedStrategy, children }: StrategyQueryProps) {
  const result = useMatchNeedsWithOffersStrategy(resolvedStrategy.hookArgs);
  return children(getRecommendationQueryState(runtime, resolvedStrategy, result));
}

const STRATEGY_QUERIES: Record<Strategies, React.ComponentType<StrategyQueryProps>> = {
  [Strategies.YourFavourites]: YourFavouritesQuery,
  [Strategies.MostPopular]: MostPopularQuery,
  [Strategies.UrgentNeeds]: UrgentNeedsQuery,
  [Strategies.RecentOffers]: RecentOffersQuery,
  [Strategies.RecentLoans]: RecentLoansQuery,
  [Strategies.YourInterest]: YourInterestQuery,
  [Strategies.ByCategory]: ByCategoryQuery,
  [Strategies.NeighbourOffers]: NeighbourOffersQuery,
  [Strategies.NeighbourRequests]: NeighbourRequestsQuery,
  [Strategies.NearestSharingPlace]: NearestSharingPlaceQuery,
  [Strategies.UseUserItemPredictions]: UserItemPredictionsQuery,
  [Strategies.UsePredictions]: PredictionsQuery,
  [Strategies.UsePastInteractions]: PastInteractionsQuery,
  [Strategies.UseTestRecommendations]: TestRecommendationsQuery,
  [Strategies.UseMixedRecommendations]: MixedRecommendationsQuery,
  [Strategies.UseOneToManySimilarListings]: OneToManySimilarListingsQuery,
  [Strategies.UseMatchNeedsWithOffers]: MatchNeedsWithOffersQuery,
};

interface RecommendationQueryProps {
  strategyProps: RecommendedListingsComponentProps;
  children: StrategyQueryProps['children'];
}

export function RecommendationQuery(props: RecommendationQueryProps) {
  const runtime = useRecommendationRuntime();
  return <RecommendationQueryWithRuntime {...props} runtime={runtime} />;
}

export function RecommendationQueryWithRuntime({
  strategyProps,
  runtime,
  children,
}: RecommendationQueryProps & { runtime: RecommendationRuntime }) {
  const Query = STRATEGY_QUERIES[strategyProps.strategy];
  const resolvedStrategy = resolveRecommendationStrategy(strategyProps, runtime);
  return (
    <Query runtime={runtime} resolvedStrategy={resolvedStrategy}>
      {children}
    </Query>
  );
}

type RecommendationSectionItem = RecommendedListing | Listing | RecentLoanListing | ClosestListingsByTag | SharingPlace;

export interface RecommendationSectionState {
  recommendations: RecommendationSectionItem[] | undefined;
  isPending: boolean;
  show: boolean;
}

type RecommendationCardComponent = React.ComponentType<{
  listing: Listing | RecommendedListing;
}>;

interface RecommendedListingsProps<C extends RecommendationCardComponent = typeof RecommendedCarouselListingCard> {
  strategyProps: RecommendedListingsComponentProps;
  /** Reuses the query state already loaded by a parent, avoiding a second observer. */
  recommendationState?: RecommendationSectionState;
  recommendations?: (RecommendedListing | Listing | RecentLoanListing | ClosestListingsByTag | SharingPlace)[];
  backgroundColor?: string;
  onViewAll?: () => void;
  titleSuffix?: React.ReactNode;
  /** When provided, replaces the entire built-in title row. */
  header?: React.ReactNode;
  /** Use for standard Listing/RecommendedListing cards. */
  card?: C;
  cardProps?: Omit<React.ComponentPropsWithoutRef<C>, 'listing' | 'onPress'>;
  /** Custom renderer for non-standard recommendation items. */
  renderItem?: (
    listing: Listing | RecommendedListing | RecentLoanListing | SharingPlace,
    index: number,
  ) => React.ReactElement | null;
  emptyState?: React.ReactNode;
  extraPadding?: number;
}

function getRecommendationListings(
  recommendations: RecommendationSectionItem[] | undefined,
): (RecommendedListing | Listing | RecentLoanListing | SharingPlace)[] | undefined {
  if (!recommendations?.length) return undefined;
  if ('tagId' in (recommendations[0] ?? {})) {
    return (recommendations as ClosestListingsByTag[]).flatMap((result) => result.listings);
  }
  return recommendations as (RecommendedListing | Listing | RecentLoanListing | SharingPlace)[];
}

export function getRecommendationItemKeys(listings: RecommendationListItem[] | undefined, strategy: Strategies) {
  const seen = new Map<string, number>();
  return (
    listings?.map((item) => {
      const baseKey = getRecommendationItemBaseKey(item, strategy);
      const occurrence = seen.get(baseKey) ?? 0;
      seen.set(baseKey, occurrence + 1);
      return occurrence === 0 ? baseKey : `${baseKey}-${String(occurrence)}`;
    }) ?? []
  );
}

function shouldHideRecommendationStrategy(strategy: Strategies) {
  return (
    isFeatureDisabled(FeatureFlag.showRecommendedForYou) &&
    strategy !== Strategies.ByCategory &&
    !isNeighbourStrategy(strategy) &&
    !isSharingPlaceStrategy(strategy)
  );
}

function shouldShowRecommendationListings(
  hasRecommendationsProp: boolean,
  show: boolean,
  listings: RecommendationListItem[] | undefined,
) {
  return Boolean((hasRecommendationsProp || show) && listings?.length);
}

function RecommendationSectionHeader({
  header,
  horizontalPadding,
  onViewAll,
  strategyProps,
  titleSuffix,
}: Pick<RecommendedListingsProps, 'header' | 'onViewAll' | 'titleSuffix'> & {
  horizontalPadding: number;
  strategyProps: RecommendedListingsComponentProps;
}) {
  const { t } = useTranslation('recommendations');
  if (header) return <>{header}</>;

  return (
    <Row justifyContent="space-between" alignItems="flex-start" style={{ paddingHorizontal: horizontalPadding }}>
      <Row flex={1} paddingRight="spacing.general.x4" alignItems="center">
        <Text fontSize={20} fontWeight="700" lineHeight={24} variant="bodyBase">
          {t(getRecommendationTitleKey(strategyProps), { tagName: titleSuffix, categoryName: titleSuffix })}
        </Text>
        {typeof titleSuffix === 'string' ? null : titleSuffix}
      </Row>

      {onViewAll ? (
        <Pressable onPress={onViewAll} style={{ paddingTop: 2 }}>
          <Row alignItems="center" gap="spacing.general.x1">
            <Text color="text.interactive" fontSize={14} fontWeight="700">
              {t('landing-page:viewMore')}
            </Text>
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['icon.interactive']} />
          </Row>
        </Pressable>
      ) : null}
    </Row>
  );
}

interface RecommendationSectionBodyProps {
  backgroundColor?: string;
  emptyState?: React.ReactNode;
  extraPadding: number;
  horizontalPadding: number;
  itemKeys: string[];
  listRef: React.RefObject<FlashListRef<RecommendationListItem> | null>;
  listings: RecommendationListItem[];
  onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  renderListingItem: (info: ListRenderItemInfo<RecommendationListItem>) => React.ReactElement | null;
  shouldShowListings: boolean;
  shouldSnapOneCardAtATime: boolean;
  snapToOffsets?: number[];
  strategy: Strategies;
}

function RecommendationSectionBody({
  backgroundColor,
  emptyState,
  extraPadding,
  horizontalPadding,
  itemKeys,
  listRef,
  listings,
  onScroll,
  renderListingItem,
  shouldShowListings,
  shouldSnapOneCardAtATime,
  snapToOffsets,
  strategy,
}: RecommendationSectionBodyProps) {
  if (!shouldShowListings) return emptyState ? <>{emptyState}</> : null;

  return (
    <FlashList<RecommendationListItem>
      ref={listRef}
      horizontal
      data={listings}
      keyExtractor={(item, index) => itemKeys[index] ?? getRecommendationItemBaseKey(item, strategy)}
      disableIntervalMomentum={shouldSnapOneCardAtATime}
      drawDistance={RECOMMENDATION_LIST_DRAW_DISTANCE}
      removeClippedSubviews={false}
      scrollEventThrottle={16}
      onScroll={onScroll}
      ItemSeparatorComponent={RecommendationItemSeparator}
      renderItem={renderListingItem}
      showsHorizontalScrollIndicator={false}
      decelerationRate={shouldSnapOneCardAtATime ? 'fast' : undefined}
      snapToAlignment="start"
      snapToOffsets={snapToOffsets}
      style={{
        paddingTop: theme.spacing['spacing.general.x3'],
        backgroundColor: backgroundColor ? '#f9f9f94a' : undefined,
        ...(backgroundColor
          ? {
              shadowColor: backgroundColor,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.1,
              shadowRadius: 20,
              elevation: 3,
            }
          : {}),
      }}
      contentContainerStyle={{ paddingHorizontal: horizontalPadding + extraPadding }}
    />
  );
}

function RecommendedListingsComponent<
  C extends React.ComponentType<{
    listing: Listing | RecommendedListing;
  }> = typeof RecommendedCarouselListingCard,
>(props: RecommendedListingsProps<C>) {
  const { recommendationState } = props;
  return recommendationState ? (
    <RecommendedListingsContent {...props} recommendationState={recommendationState} />
  ) : (
    <RecommendedListingsWithQuery {...props} />
  );
}

function RecommendedListingsWithQuery<C extends RecommendationCardComponent>(props: RecommendedListingsProps<C>) {
  return (
    <RecommendationQuery strategyProps={props.strategyProps}>
      {(recommendationState) => <RecommendedListingsContent {...props} recommendationState={recommendationState} />}
    </RecommendationQuery>
  );
}

function RecommendedListingsContent<C extends RecommendationCardComponent>({
  strategyProps,
  recommendationState,
  recommendations: recommendationsProp,
  backgroundColor,
  onViewAll,
  titleSuffix,
  header,
  card,
  cardProps,
  renderItem,
  emptyState,
  extraPadding = 0,
}: RecommendedListingsProps<C> & { recommendationState: RecommendationSectionState }) {
  const navigation = useNavigation();
  const withAuthorize = useAuthorize();
  const { data: myUser } = useUser();
  const insets = useSafeAreaInsets();
  const horizontalPadding = insets.left || theme.spacing['spacing.general.x4'];
  const Card = card ?? RecommendedCarouselListingCard;
  const shouldSnapOneCardAtATime = strategyProps.strategy === Strategies.UrgentNeeds;
  const snapToInterval = LANDING_PAGE_REQUEST_CARD_WIDTH + RECOMMENDATION_ITEM_SEPARATOR_WIDTH;
  const listRef = useRef<FlashListRef<RecommendationListItem>>(null);
  const scrollOffsetXRef = useRef(0);
  const previousItemCountRef = useRef(0);
  const [shouldAnimateSnapLayout, setShouldAnimateSnapLayout] = useState(false);

  const recommendedListings = recommendationsProp ?? recommendationState.recommendations;
  const listings = getRecommendationListings(recommendedListings);
  const { isPending, show } = recommendationState;
  const shouldShowSkeleton = isPending && !listings?.length;
  const itemCount = listings?.length ?? 0;
  const itemKeys = getRecommendationItemKeys(listings, strategyProps.strategy);
  const snapToOffsets = shouldSnapOneCardAtATime
    ? Array.from({ length: itemCount }, (_, index) => index * snapToInterval)
    : undefined;

  const prepareForDismissLayoutAnimation = () => {
    if (!shouldSnapOneCardAtATime) return;
    setShouldAnimateSnapLayout(true);
    listRef.current?.prepareForLayoutAnimationRender();
  };

  useEffect(() => {
    const previousItemCount = previousItemCountRef.current;
    previousItemCountRef.current = itemCount;
    const didItemCountChange = itemCount !== previousItemCount;

    if (didItemCountChange && shouldAnimateSnapLayout) {
      requestAnimationFrame(() => setShouldAnimateSnapLayout(false));
    }

    if (!shouldSnapOneCardAtATime || itemCount === 0 || itemCount >= previousItemCount) {
      return;
    }

    const maxValidOffset = Math.max(0, (itemCount - 1) * snapToInterval);
    if (scrollOffsetXRef.current <= maxValidOffset) {
      return;
    }

    scrollOffsetXRef.current = maxValidOffset;
    requestAnimationFrame(() => {
      listRef.current?.scrollToOffset({ animated: true, offset: maxValidOffset });
    });
  }, [itemCount, shouldAnimateSnapLayout, shouldSnapOneCardAtATime, snapToInterval]);

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    scrollOffsetXRef.current = contentOffset.x;
    const isUserInitiated = contentOffset.x > 0 && contentOffset.x < contentSize.width - layoutMeasurement.width;
    if (!isUserInitiated) return;
    if (!strategyProps.recommendationType) return;
    const params = getBrowsedRecommendedListingParameters({
      user_id: myUser?.id,
      listing_type: strategyProps.recommendationType,
      strategy: strategyProps.strategy,
    });
    track(Event.BrowseRecommendedListings, params);
  };

  const onPress = (listing: RecommendedListing | Listing) =>
    withAuthorize(() => {
      const params = getClickedRecommendedListingParameters({
        user_id: myUser?.id,
        listing_id: listing.id,
        listing_type: listing.type,
        listing: listing.item.name,
        predicted_score: 'score' in listing ? listing.score : 0,
        strategy: 'strategy' in listing ? listing.strategy : strategyProps.strategy,
      });
      track(Event.ClickedRecommendedListing, params);
      navigateToListing(navigation, listing.id, Boolean(myUser?.id !== listing.user.id && listing.grouping));
    });

  const renderListingItem = ({ item, index }: ListRenderItemInfo<RecommendationListItem>) => {
    const key = itemKeys[index] ?? getRecommendationItemBaseKey(item, strategyProps.strategy);
    if (renderItem) {
      const renderedItem = <React.Fragment key={key}>{renderItem(item, index)}</React.Fragment>;
      return shouldSnapOneCardAtATime ? (
        <Animated.View layout={shouldAnimateSnapLayout ? RECOMMENDATION_ITEM_LAYOUT_TRANSITION : undefined}>
          {renderedItem}
        </Animated.View>
      ) : (
        renderedItem
      );
    }

    const standardListing = item as Listing | RecommendedListing;
    const animatedCardProps = shouldSnapOneCardAtATime
      ? ({ onBeforeDismiss: prepareForDismissLayoutAnimation } as unknown as Partial<React.ComponentPropsWithoutRef<C>>)
      : undefined;
    const renderedItem = (
      <Card
        {...(cardProps ? (cardProps as React.ComponentPropsWithoutRef<C>) : {})}
        {...animatedCardProps}
        listing={standardListing}
        isNew={strategyProps.strategy !== Strategies.RecentOffers && isNewListing(standardListing.createdAt)}
        onPress={onPress}
      />
    );

    return shouldSnapOneCardAtATime ? (
      <Animated.View layout={shouldAnimateSnapLayout ? RECOMMENDATION_ITEM_LAYOUT_TRANSITION : undefined}>
        {renderedItem}
      </Animated.View>
    ) : (
      renderedItem
    );
  };

  if (shouldHideRecommendationStrategy(strategyProps.strategy)) return null;

  if (shouldShowSkeleton) {
    return <HorizontalListingsSkeleton />;
  }

  if (!listings?.length && !emptyState) {
    return null;
  }

  const shouldShowListings = shouldShowRecommendationListings(Boolean(recommendationsProp), show, listings);

  if (!shouldShowListings && !emptyState) {
    return null;
  }

  return (
    <Box style={{ marginHorizontal: -horizontalPadding }}>
      <RecommendationSectionHeader
        header={header}
        horizontalPadding={horizontalPadding}
        onViewAll={onViewAll}
        strategyProps={strategyProps}
        titleSuffix={titleSuffix}
      />
      <RecommendationSectionBody
        backgroundColor={backgroundColor}
        emptyState={emptyState}
        extraPadding={extraPadding}
        horizontalPadding={horizontalPadding}
        itemKeys={itemKeys}
        listRef={listRef}
        listings={listings ?? []}
        onScroll={onScroll}
        renderListingItem={renderListingItem}
        shouldShowListings={shouldShowListings}
        shouldSnapOneCardAtATime={shouldSnapOneCardAtATime}
        snapToOffsets={snapToOffsets}
        strategy={strategyProps.strategy}
      />
    </Box>
  );
}

const ListingListWithStrategy = RecommendedListingsComponent;

export { ListingListWithStrategy };
